import { useCallback } from "react";

import {
  useAppDispatch,
  useAppSelector,
} from "../redux/hooks";

import {
  addWidgetSection,
  deleteWidgetSection,
} from "../redux/slices/siteContentSlice";

import {
  createDefaultWidgetData,
} from "../utils/widgetDefaults";

import {
  getWidgetIndexForOperation,
} from "../utils/widgetIndexUtils";

export const useWidgetSections = () => {
  const dispatch = useAppDispatch();

  const { data: siteContent } = useAppSelector(
    (state) => state?.siteContent,
  );

  const originalWidgets =
    siteContent?.widgets ?? [];

  const allHomeWidgets =
    siteContent?.homeWidgets ?? [];

  // ==================================================
  // VISIBLE HOME WIDGETS
  // ==================================================

  const widgets = allHomeWidgets.filter(
    (widget: any) =>
      !widget?.type
        ?.toLowerCase()
        .includes("footer"),
  );

  // ==================================================
  // VISIBLE INDEX -> STORE INDEX
  // ==================================================

  const getOriginalIndex = useCallback(
    (displayIndex: number) => {
      return getWidgetIndexForOperation(
        displayIndex,
        widgets,
        originalWidgets,
      );
    },
    [widgets, originalWidgets],
  );

  // ==================================================
  // GENERATE UNIQUE ID
  // ==================================================

  const generateWidgetId = useCallback(
    (widgetType: string): string => {
      const uuid = crypto.randomUUID();

      const shortId = uuid
        .split("-")[0]
        .toUpperCase();

      return `${widgetType}_${shortId}`;
    },
    [],
  );

  // ==================================================
  // ADD WIDGET
  // ==================================================

  const addSection = useCallback(
    (
      widgetType: string,
      afterDisplayIndex?: number,
    ) => {
      const widgetData =
        createDefaultWidgetData(
          widgetType,
        );

      const uniqueId =
        generateWidgetId(widgetType);

      const newWidget = {
        type: uniqueId,
        id: uniqueId,
        data: widgetData,
      };

      let afterIndex:
        | number
        | undefined;

      if (
        afterDisplayIndex !== undefined
      ) {
        afterIndex =
          getOriginalIndex(
            afterDisplayIndex,
          );

        if (afterIndex === -1) {
          return;
        }
      }

      dispatch(
        addWidgetSection({
          widget: newWidget,
          afterIndex,
        }),
      );
    },
    [
      dispatch,
      generateWidgetId,
      getOriginalIndex,
    ],
  );

  // ==================================================
  // DELETE WIDGET
  // ==================================================

  const deleteSection = useCallback(
    (displayIndex: number) => {
      // ----------------------------------------------
      // Get target visible widget
      // ----------------------------------------------

      const targetWidget =
        widgets[displayIndex];

      if (!targetWidget) {
        return;
      }

      // ----------------------------------------------
      // Convert visible index -> Redux index
      // ----------------------------------------------

      const widgetsIndex =
        getOriginalIndex(
          displayIndex,
        );

      if (widgetsIndex === -1) {
        return;
      }

      const storeWidget =
        originalWidgets[widgetsIndex];

      // ----------------------------------------------
      // SAFETY CHECK
      // ----------------------------------------------

      if (!storeWidget) {
        return;
      }

      // ----------------------------------------------
      // Verify ID before deletion
      // ----------------------------------------------

      if (
        targetWidget.id &&
        storeWidget.id &&
        targetWidget.id !==
          storeWidget.id
      ) {
        return;
      }

      // ----------------------------------------------
      // Prevent deleting last visible widget
      // ----------------------------------------------

      const nonFooterCount =
        widgets.length;

      if (nonFooterCount <= 1) {
        return;
      }

      // ----------------------------------------------
      // FINAL DELETE
      // ----------------------------------------------

      dispatch(
        deleteWidgetSection(
          widgetsIndex,
        ),
      );
    },
    [
      dispatch,
      widgets,
      originalWidgets,
      getOriginalIndex,
    ],
  );

  return {
    addSection,
    deleteSection,
    widgets,
    hasWidgets:
      widgets.length > 0,
  };
};